import { parseTime } from "@/utils/date/formatTime";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

export const COUNTDOWN_WINDOW_MS = 3 * HOUR_MS;
export const PRE_COUNTDOWN_WINDOW_MS = 14 * HOUR_MS;
export const HIDE_IF_WITHIN_LAST_MS = 5 * MINUTE_MS;

const SECONDS_THRESHOLD_MS = 2 * MINUTE_MS;

export type ClickAndCollectState =
  | { kind: "countdown"; timeLeftMs: number }
  | { kind: "pre" }
  | { kind: "default" };

export function getCutoffTime(
  orderByLocalTimeToday: string | null | undefined,
  reference: Date,
): Date | null {
  const parsed = parseTime(orderByLocalTimeToday);

  if (!parsed) return null;

  const cutoff = new Date(reference.getTime());
  cutoff.setHours(parsed.hours, parsed.minutes, 0, 0);
  return cutoff;
}

export function getClickAndCollectState({
  orderByLocalTimeToday,
  now,
  loadedAt,
}: {
  orderByLocalTimeToday: string | null | undefined;
  now: Date;
  loadedAt: Date;
}): ClickAndCollectState {
  const cutoff = getCutoffTime(orderByLocalTimeToday, now);

  if (!cutoff) return { kind: "default" };

  const timeLeftMs = cutoff.getTime() - now.getTime();

  if (timeLeftMs <= 0) return { kind: "default" };

  if (cutoff.getTime() - loadedAt.getTime() < HIDE_IF_WITHIN_LAST_MS) {
    return { kind: "default" };
  }

  if (timeLeftMs <= COUNTDOWN_WINDOW_MS)
    return { kind: "countdown", timeLeftMs };
  if (timeLeftMs <= PRE_COUNTDOWN_WINDOW_MS) return { kind: "pre" };

  return { kind: "default" };
}

export const showsSeconds = (timeLeftMs: number) =>
  timeLeftMs < SECONDS_THRESHOLD_MS;

const pluralise = (value: number, unit: string) =>
  `${value} ${unit}${value === 1 ? "" : "s"}`;

export function formatTimeLeft(timeLeftMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(timeLeftMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours) parts.push(pluralise(hours, "hour"));
  if (minutes) parts.push(pluralise(minutes, "min"));
  if (showsSeconds(timeLeftMs)) parts.push(pluralise(seconds, "sec"));

  return parts.join(" ");
}

export function getNextTickDelayMs(timeLeftMs: number): number | null {
  if (timeLeftMs <= 0) return null;

  if (timeLeftMs > PRE_COUNTDOWN_WINDOW_MS) {
    return timeLeftMs - PRE_COUNTDOWN_WINDOW_MS;
  }

  if (timeLeftMs > COUNTDOWN_WINDOW_MS) {
    return timeLeftMs - COUNTDOWN_WINDOW_MS;
  }

  if (showsSeconds(timeLeftMs)) {
    return timeLeftMs % 1000 || 1000;
  }

  return Math.min(
    timeLeftMs % MINUTE_MS || MINUTE_MS,
    Math.max(timeLeftMs - SECONDS_THRESHOLD_MS + 1, 1),
  );
}
